"use client";

import {useTranslation} from "react-i18next";
import {useAlert} from "@/hooks/useAlert";
import {useAssistantModelUpdate} from "@/hooks/useAssistantModelUpdate";
import {isAutoSelectableForConversation} from "@/services/wingman";
import {isAutoAssistantModel} from "@/utils/ai/modelCatalog";

/**
 * Changes an assistant's model from inside a conversation. Auto runs only on
 * Wingman v2, so it is refused, with a warning, on a legacy conversation.
 */
export function useConversationModelChange({
   conversationId,
   onModelUpdated,
}: {
   conversationId: string;
   onModelUpdated: (assistantId: string, model: string) => void;
}) {
   const {t} = useTranslation("ask");
   const {setAlert} = useAlert();
   const {updateModel, isSaving} = useAssistantModelUpdate({onModelUpdated});

   const changeModel = (assistantId: string, newModel: string): Promise<boolean> => {
      if (isAutoAssistantModel(newModel) && !isAutoSelectableForConversation(conversationId)) {
         setAlert({
            status: "warning",
            message: t("autoUnavailableOnLegacyConversation"),
            secondsToClose: 10,
         });
         return Promise.resolve(false);
      }
      return updateModel(assistantId, newModel);
   };

   return {changeModel, isSaving};
}
